import {z} from 'zod';

import {BaseVoidResponseSchema, BasePriceSchema} from '@dms/api/shared';

export const BillingRecipientSchema = z.enum([
  'OPERATOR',
  'OWNER',
  'USER',
  'MANAGER',
  'INVOICE',
  'OTHER',
  'DEFAULT',
]);
export type BillingRecipient = z.infer<typeof BillingRecipientSchema>;

export const FileMetadataSchema = z.object({
  fileId: z.string(),
  name: z.string(),
  fileUrl: z.string(),
  remoteId: z.string().nullish(),
  documentType: z
    .enum([
      'PAYMENT_INVOICE',
      'PAYMENT_PROFORMA_INVOICE',
      'INTERNAL_INVOICE_DOCUMENT',
      'PAYMENT_TAX_DOCUMENT',
      'PAYMENT_CASH_RECEIPT',
    ])
    .nullish(),
  createdAt: z.string().nullish(),
  createdBy: z.string(),
});

export const FileMetadataExtendedSchema = FileMetadataSchema.extend({
  documentType: z
    .enum([
      'PAYMENT_INVOICE',
      'PAYMENT_PROFORMA_INVOICE',
      'INTERNAL_INVOICE_DOCUMENT',
      'PAYMENT_TAX_DOCUMENT',
      'PAYMENT_CASH_RECEIPT',
      'CORRECTIVE_TAX_DOCUMENT',
    ])
    .nullish(),
});

export const AfsAdditionalCustomerTypeSchema = z.enum(['OPERATOR', 'RECIPIENT']);
export type AfsAdditionalCustomerType = z.infer<typeof AfsAdditionalCustomerTypeSchema>;

export const AbroadSaleTypeSchema = z.enum(['OUT_OF_EUROPE', 'IN_EUROPE']);
export type AbroadSaleType = z.infer<typeof AbroadSaleTypeSchema>;

export const AddressSchema = z.object({
  street: z.string(),
  descriptiveNumber: z.string().nullable(),
  orientationNumber: z.string().nullable(),
  city: z.string(),
  zip: z.string(),
  addressComplement: z.string().nullable(),
  country: z.string(),
  prefix: z.string().nullable(),
  district: z.string().nullable(),
  state: z.string().nullable(),
  postal: z.string().nullish(),
  coordinates: z
    .object({
      latitude: z.string(),
      longitude: z.string(),
    })
    .nullish(),
  externalAddressId: z.string().nullish(),
});

export type Address = z.infer<typeof AddressSchema>;

export const BankAccountSchema = z.object({
  id: z.string().nullish(),
  name: z.string().nullish(),
  countryCode: z.string().nullish(),
  ownerName: z.string().nullish(),
  iban: z.string().nullish(),
  swiftBic: z.string().nullish(),
  currency: z.string().nullish(),
  number: z.string().nullish(),
  bankCode: z.string().nullish(),
});
export type BankAccount = z.infer<typeof BankAccountSchema>;

export const IdentityCardSchema = z.object({
  id: z.string(),
  type: z.string().nullable(),
  cardNumber: z.string(),
  issuedOn: z.string().nullable(),
  validUntil: z.string().nullable(),
  issuer: z.string().nullable(),
  issuedInCountryCode: z.string().nullable(),
  note: z.string().nullable(),
});
export type IdentityCard = z.infer<typeof IdentityCardSchema>;

export const PersonSchema = z.object({
  id: z.string(),
  firstName: z.string().nullable(),
  lastName: z.string().nullable(),
  middleName: z.string().nullable(),
  titleBefore: z.string().nullable(),
  titleAfter: z.string().nullable(),
  genderKey: z.string().nullable(),
  roles: z.string().nullable(),
  citizenshipCode: z.string().nullable(),
  birthdate: z.string().nullable(),
  personalIdentifier: z.string().nullable(),
  phoneNumbers: z.array(
    z.object({
      type: z.string().nullable(),
      countryCode: z.string(),
      prefix: z.string(),
      number: z.string(),
    })
  ),
  emails: z.array(
    z.object({
      type: z.string().nullable(),
      email: z.string(),
    })
  ),
  identityCards: z.array(IdentityCardSchema),
  permanentAddress: z
    .object({
      id: z.string(),
      address: AddressSchema,
      type: z.string().nullable(),
      invalid: z.boolean().nullable(),
    })
    .nullable(),
});
export type Person = z.infer<typeof PersonSchema>;

export const BusinessInfoSchema = z.object({
  id: z.string().nullish(),
  businessInfo: z
    .object({
      countryOfRegistrationCode: z.string().nullish(),
      registrationNumber: z.string().nullish(),
      vatNumber: z.string().nullish(),
      tradeName: z.string().nullish(),
      fileNumber: z.string().nullish(),
    })
    .nullish(),
  address: z
    .object({
      id: z.string().nullish(),
      address: AddressSchema.nullish(),
      type: z.string().nullish(),
      invalid: z.boolean().nullish(),
    })
    .nullish(),
});
export type BusinessInfo = z.infer<typeof BusinessInfoSchema>;

export const BillingInformationSchema = z.object({
  customerInformation: z.object({
    id: z.string(),
  }),
  contractInformation: z
    .object({
      id: z.string().nullish(),
      permanent: z.boolean().nullish(),
      legalForm: z.enum(['LEGAL_ENTITY', 'NATURAL_PERSON', 'SELF_EMPLOYED']).nullish(),
      bankAccounts: z.array(BankAccountSchema).nullish(),
      person: PersonSchema.nullish(),
      businessInfo: BusinessInfoSchema.nullish(),
      customFieldsPayload: z
        .array(
          z.object({
            definitionId: z.string().nullish(),
            value: z
              .object({
                type: z
                  .enum(['integer', 'decimal', 'string', 'money', 'date', 'datetime', 'boolean'])
                  .nullish(),
                value: z.array(z.boolean().nullish()).nullish(),
              })
              .nullish(),
          })
        )
        .nullish(),
      isDeleted: z.boolean().nullish(),
    })
    .nullish(),
  selectedIdentityCards: z.array(IdentityCardSchema).nullish(),
  isAbroadSaleAllowed: z.boolean(),
  abroadSale: AbroadSaleTypeSchema.nullish(),
});
export type BillingInformation = z.infer<typeof BillingInformationSchema>;

export const ForeignCurrencyPaymentSchema = z.object({
  exchangeRateRatio: z.number(),
  exchangeCurrency: z.string(),
});
export type ForeignCurrencyPayment = z.infer<typeof ForeignCurrencyPaymentSchema>;

export const AftersalesPaymentMethodSchema = z.enum([
  'BANK_TRANSFER',
  'CASH',
  'CARD',
  'CASH_ON_DELIVERY',
  'INTERNAL',
]);
export type AftersalesPaymentMethod = z.infer<typeof AftersalesPaymentMethodSchema>;

export const AftersalesPaymentStateSchema = z.enum([
  'CONCEPT',
  'PENDING',
  'PAID',
  'PARTIALLY_PAID',
]);
export type AftersalesPaymentState = z.infer<typeof AftersalesPaymentStateSchema>;

export type FileMetadata = z.infer<typeof FileMetadataSchema>;

export const CheckoutNotificationApiCallSchema = z.object({
  path: z.string(),
  method: z.enum(['GET', 'POST', 'PATCH', 'PUT']),
  payload: z.record(z.any()).nullish(),
});
export type CheckoutNotificationApiCall = z.infer<typeof CheckoutNotificationApiCallSchema>;

export const CheckoutNotificationButtonSchema = z.object({
  label: z.string(),
  action: z.enum(['NAVIGATE', 'API_CALL']),
  actionTarget: z.enum(['SAME_TAB', 'NEW_TAB']).nullish(),
  navigateUrl: z.string().nullish(),
  apiCall: CheckoutNotificationApiCallSchema.nullish(),
  onSuccess: z.enum(['CHECKOUT_REFRESH', 'CHECKOUT_PAYMENT_REFRESH', 'PAGE_REFRESH']).nullish(),
});
export type CheckoutNotificationButton = z.infer<typeof CheckoutNotificationButtonSchema>;

export const CheckoutNotificationSchema = z.object({
  type: z.enum(['ERROR', 'WARNING', 'SUCCESS', 'INFO']),
  message: z.string(),
  code: z.string().nullish(),
  buttons: z.array(CheckoutNotificationButtonSchema).nullish(),
});
export type CheckoutNotification = z.infer<typeof CheckoutNotificationSchema>;

export const AfsPaymentDiscriminatorSchema = z.enum([
  'BALANCE',
  'DEPOSIT',
  'BALANCE_CANCELLATION',
  'BALANCE_CORRECTION_FULL',
  'BALANCE_CORRECTION_PARTIAL',
  'DEPOSIT_CANCELLATION',
]);
export type AfsPaymentDiscriminator = z.infer<typeof AfsPaymentDiscriminatorSchema>;

export const PaymentOrientationSchema = z.enum(['STANDARD', 'INTERNAL']);
export type PaymentOrientation = z.infer<typeof PaymentOrientationSchema>;

const PaymentFieldStateSchema = z.object({
  state: z.enum(['EDIT', 'VIEW', 'HIDDEN']),
  isRequired: z.boolean(),
});

const PaymentFieldsStateSchema = z.object({
  cashRegisterId: PaymentFieldStateSchema.nullish(),
  correctionReason: PaymentFieldStateSchema.nullish(),
  customerServiceContractId: PaymentFieldStateSchema.nullish(),
  dateOfReception: PaymentFieldStateSchema.nullish(),
  due: PaymentFieldStateSchema.nullish(),
  dueDate: PaymentFieldStateSchema.nullish(),
  incomingBankAccount: PaymentFieldStateSchema.nullish(),
  issueDate: PaymentFieldStateSchema.nullish(),
  note: PaymentFieldStateSchema.nullish(),
  paymentAmount: PaymentFieldStateSchema.nullish(),
  paymentMethod: PaymentFieldStateSchema.nullish(),
  taxableEventDate: PaymentFieldStateSchema.nullish(),
});
export type PaymentFieldsState = z.infer<typeof PaymentFieldsStateSchema>;

export const PaymentAllowedActionSchema = z.enum([
  'ISSUE_INVOICE',
  'CANCEL_INVOICE',
  'INITIALIZE_FULL_CORRECTION',
  'INITIALIZE_PARTIAL_CORRECTION',
  'ISSUE_FULL_CORRECTION',
  'CANCEL_FULL_CORRECTION',
  'DISCARD_FULL_CORRECTION',
  'ISSUE_PARTIAL_CORRECTION',
  'CANCEL_PARTIAL_CORRECTION',
  'DISCARD_PARTIAL_CORRECTION',
  'SELECT_TEMPLATE',
  'REMOVE_PAYMENT',
  'PAY',
  'SEND_DOCUMENT',
  'RESUME_INVOICE',
  'RESUME_FULL_CORRECTION',
  'RESUME_PARTIAL_CORRECTION',
]);
export type PaymentAllowedAction = z.infer<typeof PaymentAllowedActionSchema>;

export const PaymentUiCapabilitiesSchema = z.object({
  mode: z.enum(['EDIT', 'VIEW']),
  fieldState: PaymentFieldsStateSchema,
  allowedActions: z.array(PaymentAllowedActionSchema),
});
export type PaymentUiCapabilities = z.infer<typeof PaymentUiCapabilitiesSchema>;

export const PaymentSchema = z.object({
  checkoutPaymentId: z.string(),
  discriminator: AfsPaymentDiscriminatorSchema,
  orientation: PaymentOrientationSchema,
  direction: z.enum(['incoming', 'outgoing']),
  templateId: z.string().uuid().nullable().optional(),
  paymentAmount: BasePriceSchema.nullish(),
  roundedPaymentAmount: BasePriceSchema.nullish(),
  remainingPaymentAmount: BasePriceSchema.nullish(),
  exchangeRate: z.string().nullish(),
  foreignCurrencyPaymentAmount: BasePriceSchema.nullish(),
  remainingForeignCurrencyPaymentAmount: BasePriceSchema.nullish(),
  issuedOn: z.string().nullish(),
  due: z.number().nullish(),
  dueDate: z.string().nullish(),
  dateOfTaxableSupply: z.string().nullish(),
  method: AftersalesPaymentMethodSchema.nullish(),
  bankAccount: z
    .object({
      id: z.string().nullish(),
      isPrimary: z.boolean().nullish(),
      name: z.string().nullish(),
      number: z.string().nullish(),
      bankName: z.string().nullish(),
      iban: z.string().nullish(),
      swift: z.string().nullish(),
      currency: z.string().nullish(),
    })
    .nullish(),
  availablePaymentMethods: z
    .array(z.enum(['BANK_TRANSFER', 'CASH', 'CARD', 'INTERNAL', 'CASH_ON_DELIVERY']))
    .nullish(),
  cashRegisterId: z.string().nullish(),
  note: z.string().nullish(),
  paymentState: AftersalesPaymentStateSchema,
  payDate: z.string().nullish(),
  files: z.array(FileMetadataSchema).nullish(),
  invoiceId: z.string().nullish(),
  invoiceNumber: z.string().nullable().nullish(),
  invoices: z.array(FileMetadataSchema).nullish(),
  proformaInvoiceId: z.string().nullish(),
  proformaInvoices: z.array(FileMetadataSchema).nullish(),
  internalInvoiceId: z.string().nullish(),
  internalInvoices: z.array(FileMetadataSchema).nullish(),
  correctiveTaxDocuments: z.array(FileMetadataExtendedSchema).nullish(),
  correctiveTaxDocumentId: z.string().nullish(),
  taxDocumentId: z.string().nullish(),
  taxableDocuments: z.array(FileMetadataSchema).nullish(),
  cashRegisterDocumentId: z.string().nullish(),
  cashReceipts: z.array(FileMetadataSchema).nullish(),
  customerServiceContractId: z.string().nullish(),
  isCancellingAllowed: z.boolean().nullish(),
  documentNavigateUrl: z.string().nullable().nullish(),
  documentState: z
    .enum([
      'concept',
      'cancelled',
      'corrected',
      'requested',
      'request-acknowledged',
      'request-failed',
      'draft-created',
      'document-created',
      'origins-closed',
      'issued',
      'cancelling',
    ])
    .nullish(),
  description: z.string().nullable().nullish(),
  correctionDetails: z
    .object({
      dateOfReception: z.string(),
      reason: z.string(),
    })
    .nullish(),
  notifications: z.array(CheckoutNotificationSchema).nullish(),
  uiCapabilities: PaymentUiCapabilitiesSchema.nullish(),
});
export type Payment = z.infer<typeof PaymentSchema>;

export const GetCheckoutPaymentByIdApiResponseSchema = PaymentSchema.nullish();
export type GetCheckoutPaymentByIdApiResponse = z.infer<
  typeof GetCheckoutPaymentByIdApiResponseSchema
>;

export const CheckoutPaymentSummarySchema = PaymentSchema.pick({
  checkoutPaymentId: true,
  discriminator: true,
  paymentState: true,
});
export type CheckoutPaymentSummary = z.infer<typeof CheckoutPaymentSummarySchema>;

export const GetCheckoutPaymentsV2ApiResponseSchema = z
  .array(CheckoutPaymentSummarySchema)
  .nullish();
export type GetCheckoutPaymentsV2ApiResponse = z.infer<
  typeof GetCheckoutPaymentsV2ApiResponseSchema
>;

export type GetCheckoutPaymentsV2ApiArg = z.infer<typeof CheckoutIdArgSchema>;

export const PostCheckoutAdditionalCustomerApiResponseSchema = z
  .object({checkoutAdditionalCustomerId: z.string()})
  .nullish();
export type PostCheckoutAdditionalCustomerApiResponse = z.infer<
  typeof PostCheckoutAdditionalCustomerApiResponseSchema
>;

export const DeleteCheckoutAdditionalCustomerApiArgSchema = z.object({
  checkoutId: z.string(),
  additionalCustomerId: z.string(),
});
export type DeleteCheckoutAdditionalCustomerApiArg = z.infer<
  typeof DeleteCheckoutAdditionalCustomerApiArgSchema
>;

export const PatchCheckoutAdditionalCustomerApiArgSchema = z.object({
  checkoutId: z.string(),
  additionalCustomerId: z.string(),
  body: z
    .object({
      additionalCustomerType: AfsAdditionalCustomerTypeSchema,
      contractInformationId: z.string(),
    })
    .nullish(),
});
export type PatchCheckoutAdditionalCustomerApiArg = z.infer<
  typeof PatchCheckoutAdditionalCustomerApiArgSchema
>;

export const GetCheckoutAllowedCustomerApiResponseSchema = z
  .array(
    z.object({
      billingRecipient: BillingRecipientSchema,
      customerId: z.string(),
    })
  )
  .nullish();
export type GetCheckoutAllowedCustomerApiResponse = z.infer<
  typeof GetCheckoutAllowedCustomerApiResponseSchema
>;

export const AdditionalCustomerSchema = z.object({
  id: z.string(),
  customerId: z.string(),
  additionalCustomerType: AfsAdditionalCustomerTypeSchema,
  contractInformationId: z.string().nullable(),
});

export type AdditionalCustomer = z.infer<typeof AdditionalCustomerSchema>;

export const GetCheckoutBillingInformationApiResponseSchema = z
  .object({
    additionalCustomers: z.array(AdditionalCustomerSchema).nullable(),
    billingRecipient: BillingRecipientSchema,
    isEditBillingRecipientAllowed: z.boolean(),
    billingInformation: BillingInformationSchema,
  })
  .nullish();
export type GetCheckoutBillingInformationApiResponse = z.infer<
  typeof GetCheckoutBillingInformationApiResponseSchema
>;

export const PatchCheckoutForeignCurrencyPaymentDisallowApiResponseSchema = BaseVoidResponseSchema;

export type PatchCheckoutForeignCurrencyPaymentDisallowApiResponse = z.infer<
  typeof PatchCheckoutForeignCurrencyPaymentDisallowApiResponseSchema
>;

export const PostCheckoutDepositPaymentCreateApiResponseSchema = z
  .object({
    depositPaymentId: z.string(),
  })
  .nullish();
export type PostCheckoutDepositPaymentCreateApiResponse = z.infer<
  typeof PostCheckoutDepositPaymentCreateApiResponseSchema
>;

export const PostCheckoutForeignCurrencyPaymentAllowApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutForeignCurrencyPaymentAllowApiResponse = z.infer<
  typeof PostCheckoutForeignCurrencyPaymentAllowApiResponseSchema
>;

export const PostCheckoutPaymentIssueApiResponseSchema = z
  .object({
    invoiceId: z.string(),
    file: z
      .object({
        fileId: z.string(),
        pdfUrl: z.string(),
      })
      .nullish(),
  })
  .nullish();
export type PostCheckoutPaymentIssueApiResponse = z.infer<
  typeof PostCheckoutPaymentIssueApiResponseSchema
>;

export const PutCheckoutDepositPaymentAllowApiResponseSchema = z
  .object({
    depositPaymentId: z.string(),
  })
  .nullish();
export type PutCheckoutDepositPaymentAllowApiResponse = z.infer<
  typeof PutCheckoutDepositPaymentAllowApiResponseSchema
>;

export const GetCheckoutByIdApiResponseSchema = z
  .object({
    id: z.string(),
    context: z
      .object({
        target: z.enum(['service-order', 'direct-sale']),
        id: z.string(),
      })
      .nullish(),
    typeOfSale: z.enum(['DEFAULT', 'INTERNAL']),
    billingRecipient: BillingRecipientSchema,
    isEditBillingRecipientAllowed: z.boolean(),
    billingInformation: BillingInformationSchema.nullish(),
    totalPrice: z
      .object({
        withoutVat: BasePriceSchema.nullish(),
        vat: BasePriceSchema.nullish(),
        withVat: BasePriceSchema.nullish(),
      })
      .nullish(),
    invoicedDocumentsTotalPrice: z
      .object({
        withoutVat: BasePriceSchema.nullish(),
        vat: BasePriceSchema.nullish(),
        withVat: BasePriceSchema.nullish(),
      })
      .nullish(),
    invoicedDocumentsTotalPriceDifference: z
      .object({
        withoutVat: BasePriceSchema.nullish(),
        vat: BasePriceSchema.nullish(),
        withVat: BasePriceSchema.nullish(),
      })
      .nullish(),
    currency: z.string(),
    isForeignCurrencyPayment: z.boolean(),
    foreignCurrencyPayment: z
      .object({
        exchangeRateRatio: z.number(),
        exchangeCurrency: z.string(),
      })
      .nullish(),
    isEditForeignCurrencyAllowed: z.boolean(),
    isEditDepositAllowed: z.boolean(),
    isDepositAllowed: z.boolean(),
  })
  .nullish();

export const CheckoutIdPaymentIdArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
});
export type GetCheckoutPaymentByIdApiArg = z.infer<typeof CheckoutIdPaymentIdArgSchema>;

export type DeleteCheckoutDepositPaymentApiArg = z.infer<typeof CheckoutIdPaymentIdArgSchema>;

export const PatchCheckoutPaymentApiArgSchema = CheckoutIdPaymentIdArgSchema.extend({
  body: z.object({
    paymentAmount: BasePriceSchema.nullish(),
    exchangeRate: z.string().nullish(),
    foreignCurrencyPaymentAmount: BasePriceSchema.nullish(),
    issueDate: z.string(),
    due: z.number().nullish(),
    dueDate: z.string(),
    dateOfTaxableSupply: z.string(),
    method: AftersalesPaymentMethodSchema,
    customerServiceContractId: z.string().nullish(),
    templateId: z.string().uuid().nullable().optional(),
    bankAccount: z
      .object({
        isPrimary: z.boolean(),
        name: z.string().nullish(),
        number: z.string().nullish(),
        bankName: z.string().nullish(),
        iban: z.string().nullish(),
        swift: z.string().nullish(),
        currency: z.string(),
      })
      .nullish(),
    cashRegisterId: z.string().nullish(),
    note: z.string().nullish(),
    correctionDetails: z
      .object({
        dateOfReception: z.string().nullish(),
        reason: z.string().nullish(),
      })
      .nullish(),
  }),
});
export type PatchCheckoutPaymentApiArg = z.infer<typeof PatchCheckoutPaymentApiArgSchema>;

export const PostCheckoutRecordPaidApiArgSchema = CheckoutIdPaymentIdArgSchema.extend({
  body: z.object({
    payDate: z.string(),
    amount: z.number(),
    currency: z.string(),
    paymentMethod: AftersalesPaymentMethodSchema.nullish(),
    cashRegisterId: z.string().nullish(),
    withCashRegisterDocument: z.boolean(),
    tenantBankAccount: z
      .object({
        name: z.string().nullish(),
        accountName: z.string(),
        accountNumber: z.string(),
        isPrimary: z.boolean(),
        currencyCode: z.string(),
        swift: z.string().nullish(),
        iban: z.string().nullish(),
      })
      .nullish(),
  }),
});
export type PostCheckoutRecordPaidApiArg = z.infer<typeof PostCheckoutRecordPaidApiArgSchema>;

export const CheckoutIdArgSchema = z.object({
  checkoutId: z.string(),
});
export type GetCheckoutByIdApiArg = z.infer<typeof CheckoutIdArgSchema>;

export const PutCheckoutDepositPaymentAllowApiArgSchema = CheckoutIdArgSchema.extend({
  balancePaymentId: z.string().optional(),
});
export type PutCheckoutDepositPaymentAllowApiArg = z.infer<
  typeof PutCheckoutDepositPaymentAllowApiArgSchema
>;

export const PutCheckoutDepositPaymentDisallowApiArgSchema = CheckoutIdArgSchema.extend({
  balancePaymentId: z.string().optional(),
});
export type PutCheckoutDepositPaymentDisallowApiArg = z.infer<
  typeof PutCheckoutDepositPaymentDisallowApiArgSchema
>;

export type GetCheckoutAllowedCustomerApiArg = z.infer<typeof CheckoutIdArgSchema>;

export type GetCheckoutBillingInformationApiArg = z.infer<typeof CheckoutIdArgSchema>;

export type PatchCheckoutForeignCurrencyPaymentDisallowApiArg = z.infer<typeof CheckoutIdArgSchema>;

export type PostCheckoutDepositPaymentCreateApiArg = z.infer<typeof CheckoutIdArgSchema>;

export const PutCheckoutSetBillingInformationApiArgSchema = CheckoutIdArgSchema.extend({
  body: z.object({
    billingRecipient: BillingRecipientSchema,
    customerId: z.string(),
  }),
});

export type PutCheckoutSetBillingInformationApiArg = z.infer<
  typeof PutCheckoutSetBillingInformationApiArgSchema
>;

export const PostCheckoutAdditionalCustomerApiArgSchema = CheckoutIdArgSchema.extend({
  body: z
    .object({
      customerId: z.string(),
      additionalCustomerType: AfsAdditionalCustomerTypeSchema,
    })
    .nullish(),
});
export type PostCheckoutAdditionalCustomerApiArg = z.infer<
  typeof PostCheckoutAdditionalCustomerApiArgSchema
>;

export const PostCheckoutForeignCurrencyPaymentAllowApiArgSchema = CheckoutIdArgSchema.extend({
  body: ForeignCurrencyPaymentSchema.nullish(),
});

export type PostCheckoutForeignCurrencyPaymentAllowApiArg = z.infer<
  typeof PostCheckoutForeignCurrencyPaymentAllowApiArgSchema
>;

export const PutCheckoutSetContractInformationApiArgSchema = CheckoutIdArgSchema.extend({
  body: z
    .object({
      customerContractInformationId: z.string(),
    })
    .nullish(),
});
export type PutCheckoutSetContractInformationApiArg = z.infer<
  typeof PutCheckoutSetContractInformationApiArgSchema
>;

export type GetCheckoutByIdApiResponse = z.infer<typeof GetCheckoutByIdApiResponseSchema>;

export const PutCheckoutDepositPaymentDisallowApiResponseSchema = BaseVoidResponseSchema;

export type PutCheckoutDepositPaymentDisallowApiResponse = z.infer<
  typeof PutCheckoutDepositPaymentDisallowApiResponseSchema
>;

export const PutCheckoutSetBillingInformationApiResponseSchema = BaseVoidResponseSchema;

export type PutCheckoutSetBillingInformationApiResponse = z.infer<
  typeof PutCheckoutSetBillingInformationApiResponseSchema
>;

export const PutCheckoutSetContractInformationApiResponseSchema = BaseVoidResponseSchema;

export type PutCheckoutSetContractInformationApiResponse = z.infer<
  typeof PutCheckoutSetContractInformationApiResponseSchema
>;

export const PatchCheckoutPaymentApiResponseSchema = BaseVoidResponseSchema;

export type PatchCheckoutPaymentApiResponse = z.infer<typeof PatchCheckoutPaymentApiResponseSchema>;

export const DeleteCheckoutAdditionalCustomerApiResponseSchema = BaseVoidResponseSchema;

export type DeleteCheckoutAdditionalCustomerApiResponse = z.infer<
  typeof DeleteCheckoutAdditionalCustomerApiResponseSchema
>;

export const DeleteCheckoutDepositPaymentApiResponseSchema = BaseVoidResponseSchema;

export type DeleteCheckoutDepositPaymentApiResponse = z.infer<
  typeof DeleteCheckoutDepositPaymentApiResponseSchema
>;

export const PatchCheckoutAdditionalCustomerApiResponseSchema = BaseVoidResponseSchema;

export type PatchCheckoutAdditionalCustomerApiResponse = z.infer<
  typeof PatchCheckoutAdditionalCustomerApiResponseSchema
>;

export const PostCheckoutRecordPaidApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutRecordPaidApiResponse = z.infer<
  typeof PostCheckoutRecordPaidApiResponseSchema
>;

export const GetCheckoutLinkForServiceOrderApiArgSchema = z.object({serviceOrderId: z.string()});

export type GetCheckoutLinkForServiceOrderApiArg = z.infer<
  typeof GetCheckoutLinkForServiceOrderApiArgSchema
>;

export const GetCheckoutLinkForServiceOrderApiResponseSchema = z.object({
  checkoutId: z.string(),
  contextTarget: z.string(),
  contextId: z.string(),
});

export type GetCheckoutLinkForServiceOrderApiResponse = z.infer<
  typeof GetCheckoutLinkForServiceOrderApiResponseSchema
>;

export type GetCheckoutPaymentIssueRestrictionApiArg = z.infer<typeof CheckoutIdPaymentIdArgSchema>;

export const GetCheckoutPaymentIssueRestrictionApiResponseSchema = z.array(
  z.object({message: z.string(), code: z.string()})
);

export type GetCheckoutPaymentIssueRestrictionApiResponse = z.infer<
  typeof GetCheckoutPaymentIssueRestrictionApiResponseSchema
>;

export const PutCheckoutSetTypeOfSaleApiResponseSchema = BaseVoidResponseSchema;

export type PutCheckoutSetTypeOfSaleApiResponse = z.infer<
  typeof PutCheckoutSetTypeOfSaleApiResponseSchema
>;

export const PutCheckoutSetTypeOfSaleApiArgSchema = z.object({
  checkoutId: z.string(),
  body: z.object({
    typeOfSale: z.enum(['DEFAULT', 'INTERNAL']),
  }),
});
export type PutCheckoutSetTypeOfSaleApiArg = z.infer<typeof PutCheckoutSetTypeOfSaleApiArgSchema>;

export const PostCheckoutAllowAbroadSaleApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutAllowAbroadSaleApiResponse = z.infer<
  typeof PostCheckoutAllowAbroadSaleApiResponseSchema
>;

export const PostCheckoutAllowAbroadSaleApiArgSchema = z.object({
  checkoutId: z.string(),
  body: z.object({
    abroadSale: AbroadSaleTypeSchema,
  }),
  // used only for invalidating service order job totals
  // https://carvago.atlassian.net/browse/T20-99872
  recordId: z.string().nullish(),
});
export type PostCheckoutAllowAbroadSaleApiArg = z.infer<
  typeof PostCheckoutAllowAbroadSaleApiArgSchema
>;

export const PostCheckoutDisallowAbroadSaleApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutDisallowAbroadSaleApiResponse = z.infer<
  typeof PostCheckoutDisallowAbroadSaleApiResponseSchema
>;

export const PostCheckoutDisallowAbroadSaleApiArgSchema = z.object({
  checkoutId: z.string(),
  // used only for invalidating service order job totals
  // https://carvago.atlassian.net/browse/T20-99872
  recordId: z.string().nullish(),
});
export type PostCheckoutDisallowAbroadSaleApiArg = z.infer<
  typeof PostCheckoutDisallowAbroadSaleApiArgSchema
>;

export const GetCheckoutPaymentTransactionsApiResponseSchema = z.array(
  z.object({
    allowedActions: z
      .array(z.enum(['TRANSACTION_CANCELLATION', 'TAX_DOCUMENT_CREATION', 'SEND_DOCUMENT']))
      .nullish(),
    amount: BasePriceSchema,
    paymentId: z.string(),
    transactionId: z.string(),
    paymentType: z.enum(['bank_transfer', 'cash', 'payment_card', 'free_of_charge', 'offset']),
    paymentDate: z.string(),
    cashRegisterDocumentId: z.string().nullish(),
    cashRegisterDocumentNumber: z.string().nullish(),
    taxDocumentForPaymentId: z.string().nullish(),
    taxDocumentForPaymentNumber: z.string().nullish(),
    correctiveTaxDocumentForTaxDocumentForPaymentId: z.string().nullish(),
    correctiveTaxDocumentForTaxDocumentForPaymentNumber: z.string().nullish(),
    cancelledTransactionId: z.string().nullish(),
    cancelingTransactionId: z.string().nullish(),
    source: z.enum(['external', 'internal']),
  })
);

export type GetCheckoutPaymentTransactionsApiResponse = z.infer<
  typeof GetCheckoutPaymentTransactionsApiResponseSchema
>;

export const GetCheckoutPaymentTransactionsApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
});

export type GetCheckoutPaymentTransactionsApiArg = z.infer<
  typeof GetCheckoutPaymentTransactionsApiArgSchema
>;

export const PostCheckoutPaymentTransactionCancelApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutPaymentTransactionCancelApiResponse = z.infer<
  typeof PostCheckoutPaymentTransactionCancelApiResponseSchema
>;

export const PostCheckoutPaymentTransactionCancelApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  transactionId: z.string(),
});

export type PostCheckoutPaymentTransactionCancelApiArg = z.infer<
  typeof PostCheckoutPaymentTransactionCancelApiArgSchema
>;

export const PostCheckoutPaymentTransactionIssueTaxDocumentApiResponseSchema =
  BaseVoidResponseSchema;

export type PostCheckoutPaymentTransactionIssueTaxDocumentApiResponse = z.infer<
  typeof PostCheckoutPaymentTransactionIssueTaxDocumentApiResponseSchema
>;

export const PostCheckoutPaymentTransactionIssueTaxDocumentApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  transactionId: z.string(),
});

export type PostCheckoutPaymentTransactionIssueTaxDocumentApiArg = z.infer<
  typeof PostCheckoutPaymentTransactionIssueTaxDocumentApiArgSchema
>;

export const PostCheckoutPayBankTransferApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutPayBankTransferApiResponse = z.infer<
  typeof PostCheckoutPayBankTransferApiResponseSchema
>;

export const PostCheckoutPayBankTransferApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  body: z.object({
    payDate: z.string(),
    amount: z.number(),
    currency: z.string(),
    withTaxDocument: z.boolean(),
    bankAccount: z
      .object({
        isPrimary: z.boolean(),
        name: z.string().nullish(),
        number: z.string().nullish(),
        bankName: z.string().nullish(),
        iban: z.string().nullish(),
        swift: z.string().nullish(),
        currency: z.string(),
      })
      .nullish(),
  }),
});

export type PostCheckoutPayBankTransferApiArg = z.infer<
  typeof PostCheckoutPayBankTransferApiArgSchema
>;

export const PostCheckoutPayCardApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutPayCardApiResponse = z.infer<typeof PostCheckoutPayCardApiResponseSchema>;

export const PostCheckoutPayCardApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  body: z.object({
    payDate: z.string(),
    amount: z.number(),
    currency: z.string(),
    cashRegisterId: z.string().nullish(),
    withTaxDocument: z.boolean(),
  }),
});

export type PostCheckoutPayCardApiArg = z.infer<typeof PostCheckoutPayCardApiArgSchema>;

export const PostCheckoutPayCardReceiptApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutPayCardReceiptApiResponse = z.infer<
  typeof PostCheckoutPayCardReceiptApiResponseSchema
>;

export const PostCheckoutPayCardReceiptApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  body: z.object({
    payDate: z.string(),
    amount: z.number(),
    currency: z.string(),
    cashRegisterId: z.string().nullish(),
    withTaxDocument: z.boolean(),
  }),
});

export type PostCheckoutPayCardReceiptApiArg = z.infer<
  typeof PostCheckoutPayCardReceiptApiArgSchema
>;

export const PostCheckoutPayCashApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutPayCashApiResponse = z.infer<typeof PostCheckoutPayCashApiResponseSchema>;

export const PostCheckoutPayCashApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  body: z.object({
    payDate: z.string(),
    amount: z.number(),
    currency: z.string(),
    cashRegisterId: z.string().nullish(),
    withTaxDocument: z.boolean(),
  }),
});

export type PostCheckoutPayCashApiArg = z.infer<typeof PostCheckoutPayCashApiArgSchema>;

export const PostCheckoutPayCashReceiptApiResponseSchema = BaseVoidResponseSchema;

export type PostCheckoutPayCashReceiptApiResponse = z.infer<
  typeof PostCheckoutPayCashReceiptApiResponseSchema
>;

export const PostCheckoutPayCashReceiptApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  body: z.object({
    payDate: z.string(),
    amount: z.number(),
    currency: z.string(),
    cashRegisterId: z.string().nullish(),
    withTaxDocument: z.boolean(),
  }),
});

export type PostCheckoutPayCashReceiptApiArg = z.infer<
  typeof PostCheckoutPayCashReceiptApiArgSchema
>;

export const CorrectiveTaxDocumentTypeSchema = z.enum(['full-correction', 'partial-correction']);
export type CorrectiveTaxDocumentType = z.infer<typeof CorrectiveTaxDocumentTypeSchema>;

export const InvoiceTypeSchema = z.enum([
  'INVOICE',
  'PROFORMA_INVOICE',
  'BALANCE_INVOICE',
  'CORRECTIVE_TAX_DOCUMENT',
  'TAX_DOCUMENT_FOR_PAYMENT',
  'INVOICE_CANCELLATION',
  'BALANCE_INVOICE_CANCELLATION',
  'PROFORMA_INVOICE_CANCELLATION',
  'INVOICE_CORRECTION',
  'PROFORMA_INVOICE_CORRECTION',
  'BALANCE_INVOICE_CORRECTION',
]);
export type InvoiceType = z.infer<typeof InvoiceTypeSchema>;

export const CustomerDetailsSchema = z.object({
  contractInformationId: z.string().nullish(),
  name: z.string().nullish(),
  tradeName: z.string().nullish(),
  addressLine1: z.string().nullish(),
  addressLine2: z.string().nullish(),
  street: z.string().nullish(),
  descriptiveNumber: z.string().nullish(),
  orientationNumber: z.string().nullish(),
  addressComplement: z.string().nullish(),
  prefix: z.string().nullish(),
  district: z.string().nullish(),
  state: z.string().nullish(),
  postal: z.string().nullish(),
  city: z.string().nullish(),
  zip: z.string().nullish(),
  country: z.string().nullish(),
  cin: z.string().nullish(),
  tin: z.string().nullish(),
});
export type CustomerDetails = z.infer<typeof CustomerDetailsSchema>;

export const PaymentInfoSchema = z.object({
  paymentMethod: AftersalesPaymentMethodSchema,
  bankAccount: z.string().nullish(),
  bankName: z.string().nullish(),
  iban: z.string().nullish(),
  swift: z.string().nullish(),
  variableSymbol: z.string().nullish(),
  constantSymbol: z.string().nullish(),
  specificSymbol: z.string().nullish(),
});
export type PaymentInfo = z.infer<typeof PaymentInfoSchema>;

export const AdditionalCustomerCorrTaxDocSchema = z.object({
  customerId: z.string(),
  contractInformationId: z.string(),
  type: z.string(),
});
export type AdditionalCustomerCorrTaxDoc = z.infer<typeof AdditionalCustomerCorrTaxDocSchema>;

export const CustomFieldSchema = z.object({
  definitionId: z.string(),
  value: z.object({
    type: z.string(),
    value: z.any(),
  }),
});
export type CustomField = z.infer<typeof CustomFieldSchema>;

export const ExchangeRateSchema = z.object({
  amount: z.number(),
  ratio: z.string(),
  currency: z.string(),
});
export type ExchangeRate = z.infer<typeof ExchangeRateSchema>;

export const DiscountSchema = z.object({
  relatedInvoiceItemId: z.string(),
  percent: z.number(),
  originalPrice: z.number(),
  priceAfterDiscount: z.number(),
  discountValue: z.number(),
  currency: z.string(),
});
export type Discount = z.infer<typeof DiscountSchema>;

export const CorrectiveTaxDocumentItemSchema = z.object({
  quantity: z.number(),
  priceUnit: z.number(),
  isPriceUnitWithVat: z.boolean(),
  vatType: z.string(),
  currency: z.string(),
  type: z.string(),
  discount: DiscountSchema.nullish(),
});
export type CorrectiveTaxDocumentItem = z.infer<typeof CorrectiveTaxDocumentItemSchema>;

export const AllowedActionSchema = z.enum([
  'EDIT_ADDITIONAL_CUSTOMER',
  'EDIT_DATES',
  'EDIT_PAYMENT_DETAILS',
  'EDIT_CORRECTIVE_DETAILS',
  'EDIT_ITEMS',
  'EDIT_NOTES',
]);
export type AllowedAction = z.infer<typeof AllowedActionSchema>;

export const PostCorrectiveTaxDocumentItemSchema = z.object({
  invoiceItemId: z.string(),
  name: z.string(),
  quantity: z.number(),
  pricePerUnit: z.number(),
  priceWithVat: z.number(),
  priceWithoutVat: z.number(),
  unit: z.string(),
  isPriceUnitWithVat: z.boolean(),
  vatType: z.string(),
  currency: z.string(),
  type: z.string(),
  relatedInvoiceItemId: z.string().optional(),
  discount: DiscountSchema.nullish(),
});
export type PostCorrectiveTaxDocumentItem = z.infer<typeof PostCorrectiveTaxDocumentItemSchema>;

export const PostCorrectiveTaxDocumentForCreatingApiResponseSchema = z.object({
  correctiveTaxDocumentType: CorrectiveTaxDocumentTypeSchema,
  invoiceId: z.string(),
  invoiceType: InvoiceTypeSchema,
  documentSeriesId: z.string(),
  issueDate: z.string(),
  dueDate: z.string(),
  dateOfTaxableSupply: z.string(),
  dateOfReception: z.string(),
  customerId: z.string(),
  customerDetails: CustomerDetailsSchema.nullish(),
  paymentInfo: PaymentInfoSchema.nullish(),
  currency: z.string(),
  description: z.string().nullish(),
  behindItemsNote: z.string().nullish(),
  additionalCustomer: AdditionalCustomerCorrTaxDocSchema.nullish(),
  customFields: z.array(CustomFieldSchema).nullish(),
  exchangeRate: ExchangeRateSchema.nullish(),
  invoiceCategory: z.string().nullish(),
  items: z.array(PostCorrectiveTaxDocumentItemSchema).nullish(),
  allowedActions: z.array(AllowedActionSchema.nullish()).nullish(),
});

export type PostCorrectiveTaxDocumentForCreatingApiResponse = z.infer<
  typeof PostCorrectiveTaxDocumentForCreatingApiResponseSchema
>;

export const PostCorrectiveTaxDocumentForCreatingApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  body: z.object({
    correctiveTaxDocumentType: CorrectiveTaxDocumentTypeSchema,
  }),
});

export type PostCorrectiveTaxDocumentForCreatingApiArg = z.infer<
  typeof PostCorrectiveTaxDocumentForCreatingApiArgSchema
>;

export const PostCorrectiveTaxDocumentIssueApiResponseSchema = z.object({
  correctiveTaxDocumentId: z.string(),
  externalId: z.string(),
  file: z
    .object({
      fileId: z.string(),
      pdfUrl: z.string(),
    })
    .nullish(),
});

export type PostCorrectiveTaxDocumentIssueApiResponse = z.infer<
  typeof PostCorrectiveTaxDocumentIssueApiResponseSchema
>;

export const PostCorrectiveTaxDocumentIssueApiArgSchema = z.object({
  checkoutId: z.string(),
  paymentId: z.string(),
  body: z.object({
    correctiveTaxDocumentType: CorrectiveTaxDocumentTypeSchema,
    issueDate: z.string(),
    dueDate: z.string(),
    dateOfTaxableSupply: z.string(),
    dateOfReception: z.string(),
    paymentInfo: PaymentInfoSchema,
    reason: z.string(),
    behindItemsNote: z.string().nullish(),
    internalNote: z.string().nullish(),
    additionalCustomer: AdditionalCustomerCorrTaxDocSchema.nullish(),
    customFields: z.array(CustomFieldSchema).nullish(),
    exchangeRate: ExchangeRateSchema.nullish(),
    invoiceCategory: z.string().nullish(),
    items: z.array(CorrectiveTaxDocumentItemSchema).nullish(),
  }),
});

export type PostCorrectiveTaxDocumentIssueApiArg = z.infer<
  typeof PostCorrectiveTaxDocumentIssueApiArgSchema
>;

export const PostCheckoutForServiceOrderApiArgSchema = z.object({
  serviceOrderId: z.string(),
});
export type PostCheckoutForServiceOrderApiArg = z.infer<
  typeof PostCheckoutForServiceOrderApiArgSchema
>;

export const PostCheckoutForServiceOrderApiResponseSchema = z
  .object({
    checkoutId: z.string(),
  })
  .nullable();
export type PostCheckoutForServiceOrderApiResponse = z.infer<
  typeof PostCheckoutForServiceOrderApiResponseSchema
>;

export const CheckoutPaymentTemplateSchema = z.object({
  id: z.string().uuid(),
  title: z.string(),
  isDefault: z.boolean(),
  authorizationProfileId: z.string().uuid().nullable(),
});
export type CheckoutPaymentTemplate = z.infer<typeof CheckoutPaymentTemplateSchema>;

export const GetCheckoutPaymentTemplatesApiResponseSchema = z.array(CheckoutPaymentTemplateSchema);
export type GetCheckoutPaymentTemplatesApiResponse = z.infer<
  typeof GetCheckoutPaymentTemplatesApiResponseSchema
>;

export type GetCheckoutPaymentTemplatesApiArg = z.infer<typeof CheckoutIdPaymentIdArgSchema>;

export const PostCheckoutPaymentCorrectionCreateApiResponseSchema = z
  .object({checkoutPaymentId: z.string()})
  .nullish();
export type PostCheckoutPaymentCorrectionCreateApiResponse = z.infer<
  typeof PostCheckoutPaymentCorrectionCreateApiResponseSchema
>;

export type PostCheckoutPaymentFullCorrectionCreateApiArg = z.infer<
  typeof CheckoutIdPaymentIdArgSchema
>;
export type PostCheckoutPaymentPartialCorrectionCreateApiArg = z.infer<
  typeof CheckoutIdPaymentIdArgSchema
>;

export const PostCheckoutPaymentCorrectionCancelApiResponseSchema = BaseVoidResponseSchema;
export type PostCheckoutPaymentCorrectionCancelApiResponse = z.infer<
  typeof PostCheckoutPaymentCorrectionCancelApiResponseSchema
>;

export type PostCheckoutPaymentFullCorrectionCancelApiArg = z.infer<
  typeof CheckoutIdPaymentIdArgSchema
>;
export type PostCheckoutPaymentPartialCorrectionCancelApiArg = z.infer<
  typeof CheckoutIdPaymentIdArgSchema
>;

export const PostCheckoutPaymentCorrectionDiscardApiResponseSchema = BaseVoidResponseSchema;
export type PostCheckoutPaymentCorrectionDiscardApiResponse = z.infer<
  typeof PostCheckoutPaymentCorrectionDiscardApiResponseSchema
>;

export type PostCheckoutPaymentFullCorrectionDiscardApiArg = z.infer<
  typeof CheckoutIdPaymentIdArgSchema
>;
export type PostCheckoutPaymentPartialCorrectionDiscardApiArg = z.infer<
  typeof CheckoutIdPaymentIdArgSchema
>;

export type PostCheckoutPaymentFullCorrectionIssueApiArg = z.infer<
  typeof CheckoutIdPaymentIdArgSchema
>;
export type PostCheckoutPaymentPartialCorrectionIssueApiArg = z.infer<
  typeof CheckoutIdPaymentIdArgSchema
>;
export type PostCheckoutPaymentFullCorrectionIssueApiResponse = PostCheckoutPaymentIssueApiResponse;
export type PostCheckoutPaymentPartialCorrectionIssueApiResponse =
  PostCheckoutPaymentIssueApiResponse;

export type PostCheckoutPaymentInvoiceCancelApiArg = z.infer<typeof CheckoutIdPaymentIdArgSchema>;
export const PostCheckoutPaymentInvoiceCancelApiResponseSchema = BaseVoidResponseSchema;
export type PostCheckoutPaymentInvoiceCancelApiResponse = z.infer<
  typeof PostCheckoutPaymentInvoiceCancelApiResponseSchema
>;

export type PostCheckoutPaymentInvoiceIssueApiArg = z.infer<typeof CheckoutIdPaymentIdArgSchema>;
export type PostCheckoutPaymentInvoiceIssueApiResponse = PostCheckoutPaymentIssueApiResponse;

export const CheckoutPaymentIssueResumeStepSchema = z.enum([
  'verify-preconditions',
  'issue-document',
  'close-origins',
  'generate-printout',
]);
export type CheckoutPaymentIssueResumeStep = z.infer<typeof CheckoutPaymentIssueResumeStepSchema>;

export const PostCheckoutPaymentIssueResumeApiArgSchema = CheckoutIdPaymentIdArgSchema.extend({
  body: z.object({step: CheckoutPaymentIssueResumeStepSchema}),
});
export type PostCheckoutPaymentIssueResumeApiArg = z.infer<
  typeof PostCheckoutPaymentIssueResumeApiArgSchema
>;
export type PostCheckoutPaymentIssueResumeApiResponse = PostCheckoutPaymentIssueApiResponse;

export const CheckoutPaymentIntegrationNameSchema = z.enum([
  'CEBIA',
  'BOSCH_FLEET',
  'HYUNDAI_CRM_EU',
  'HYUNDAI_EHISTORIE',
]);
export type CheckoutPaymentIntegrationName = z.infer<typeof CheckoutPaymentIntegrationNameSchema>;

export const CheckoutPaymentIntegrationStateSchema = z.object({
  integration: CheckoutPaymentIntegrationNameSchema.nullish(),
  state: z.enum(['success', 'failure']).nullish(),
  message: z.string().nullish(),
  attemptedAt: z.string().nullish(),
  rerunAllowed: z.boolean().nullish(),
});
export type CheckoutPaymentIntegrationState = z.infer<typeof CheckoutPaymentIntegrationStateSchema>;

export const GetCheckoutPaymentIntegrationsApiResponseSchema = z
  .array(CheckoutPaymentIntegrationStateSchema)
  .nullish();

export const GetCheckoutIntegrationsApiArgSchema = CheckoutIdArgSchema.extend({
  paymentIds: z.array(z.string()),
});
export type GetCheckoutIntegrationsApiArg = z.infer<typeof GetCheckoutIntegrationsApiArgSchema>;

export const CheckoutPaymentIntegrationsSchema = z.object({
  paymentId: z.string(),
  integrations: z.array(CheckoutPaymentIntegrationStateSchema),
});
export type CheckoutPaymentIntegrations = z.infer<typeof CheckoutPaymentIntegrationsSchema>;

export const GetCheckoutIntegrationsApiResponseSchema = z.array(CheckoutPaymentIntegrationsSchema);
export type GetCheckoutIntegrationsApiResponse = z.infer<
  typeof GetCheckoutIntegrationsApiResponseSchema
>;

export const PostCheckoutPaymentIntegrationRerunApiArgSchema = CheckoutIdPaymentIdArgSchema.extend({
  body: z.object({integration: CheckoutPaymentIntegrationNameSchema}),
});
export type PostCheckoutPaymentIntegrationRerunApiArg = z.infer<
  typeof PostCheckoutPaymentIntegrationRerunApiArgSchema
>;
export const PostCheckoutPaymentIntegrationRerunApiResponseSchema = BaseVoidResponseSchema;
export type PostCheckoutPaymentIntegrationRerunApiResponse = z.infer<
  typeof PostCheckoutPaymentIntegrationRerunApiResponseSchema
>;
